import { Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownMenuData, DropdownMenuItem, DropdownModule } from '@vg-constellation/angular-17/dropdown';
import { AccountOwnerStrategy, PersonOwnerData } from '../account-owner-strategy.interface';
import { AccountOwnerType } from '../../../../types/AccountOwnerType';
import { AccountType } from '../../../../types/AccountType';
import { GlobalStateStore } from '@services/global-state/global-state.service';
import { getUuid } from '@models/estateProfile.model';
import * as accountDetailsContent from '@services/content/guided-flow/account-details-content';
import { OwnerType } from "../../../../types/OwnerType";
import { WebLogger } from "@weps/shared-http";
import { DropdownTypeaheadDirective } from "@weps/shared-ui";

/**
 * Person Owner Strategy Component
 *
 * Handles owner selection for individual persons (Primary, Spouse, Joint).
 * Implements AccountOwnerStrategy interface.
 *
 * Responsibilities:
 * - Generate dropdown options for Primary, Spouse, and Joint ownership
 * - Handle owner selection changes
 * - Validate owner selection
 * - Provide owner data in standardized format
 * - Support edit mode with pre-selected owner
 */
@Component({
  selector: 'app-person-owner',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    DropdownTypeaheadDirective
  ],
  templateUrl: './person-owner.component.html',
  styleUrls: ['./person-owner.component.scss']
})
export class PersonOwnerComponent implements OnInit, AccountOwnerStrategy {
  readonly globalStore = inject(GlobalStateStore);
  readonly cdr = inject(ChangeDetectorRef);
  readonly ownerType = AccountOwnerType.INDIVIDUAL_BY_NAME;
  readonly accountDetailsContent = accountDetailsContent;

  // Inputs
  @Input() accountType: AccountType;
  @Input() isEditMode: boolean = false;
  @Input() isJointOwnerToggleEnabled: boolean = false;

  // Outputs
  @Output() ownerChange = new EventEmitter<void>();
  @Output() strategyReady = new EventEmitter<AccountOwnerStrategy>();

  // Data binding
  selectedOwnerId: string | null = null;
  ownerOptions: DropdownMenuData = { groups: [] };
  validationError: string | null = null;

  // Cached data
  private primaryClient: any = null;
  private spouse: any = null;

  ngOnInit(): void {
    WebLogger.log({ message: "PersonOwner component initializing", payload: {
      accountType: this.accountType,
      isEditMode: this.isEditMode,
      isJointOwnerToggleEnabled: this.isJointOwnerToggleEnabled
    }});

    // Load estate profile data
    this.loadEstateProfileData();

    // Generate owner options
    this.ownerOptions = this.getOwnerOptions();

    // Register this strategy with parent container
    this.strategyReady.emit(this);
  }

  /**
   * Loads primary client and spouse data from estate profile
   */
  private loadEstateProfileData(): void {
    const estateProfile = this.globalStore.estateProfile?.();
    if (!estateProfile) {
      WebLogger.log({ message: "PersonOwner estate profile not available", level: "warn" });
      return;
    }

    this.primaryClient = estateProfile.primaryClient;
    this.spouse = this.globalStore.findSpouseData();

    WebLogger.log({ message: 'PersonOwner loaded estate profile data', payload: {
      hasPrimaryClient: !!this.primaryClient,
      hasSpouse: !!this.spouse
    }});
  }

  /**
   * Generates dropdown options for person owner selection
   * Implements AccountOwnerStrategy.getOwnerOptions()
   */
  getOwnerOptions(): DropdownMenuData {
    const items: DropdownMenuItem[] = [];

    if (!this.primaryClient) {
      WebLogger.log({ message: "PersonOwner cannot generate options - primary client not available", level: "warn" });
      return { groups: [] };
    }

    // Add Primary client option
    const primaryClientName = `${this.primaryClient.firstName} ${this.primaryClient.lastName}`;
    const primaryClientId = this.primaryClient.piClientId || this.primaryClient.uuid;

    items.push({
      labelText: primaryClientName,
      id: primaryClientId
    });

    // Add Spouse option if exists
    if (this.spouse) {
      const spouseName = `${this.spouse.firstName} ${this.spouse.lastName}`;
      const spouseId = this.spouse.piClientId || this.spouse.uuid;

      items.push({
        labelText: spouseName,
        id: spouseId
      });

      // Add Joint ownership options if enabled and not a retirement account
      if (this.isJointOwnerToggleEnabled && this.accountType !== AccountType.RETIREMENT_ANNUITY) {
        const jointOptions = Object.values(accountDetailsContent.JOINT_OWNERSHIP_TYPES).map(type => ({
          labelText: type.labelText,
          id: type.id
        }));
        items.push(...jointOptions);
      }
    }

    WebLogger.log({ message: "PersonOwner generated owner options", payload: { optionsCount: items.length } });

    return {
      groups: [{
        items: items
      }]
    };
  }

  /**
   * Handles dropdown selection change
   */
  onOwnerSelectionChange(event: any): void {
    this.selectedOwnerId = event.activeItems[0];
    WebLogger.log({ message: 'PersonOwner owner selection changed', payload: { selectedOwnerId: this.selectedOwnerId } });

    // Clear validation error on change
    this.validationError = null;

    // Notify parent container
    this.ownerChange.emit();
  }

  /**
   * Validates current owner selection
   * Implements AccountOwnerStrategy.validateOwnerSelection()
   */
  validateOwnerSelection(): boolean {
    if (!this.selectedOwnerId) {
      this.validationError = 'Please select an account owner';
      return false;
    }

    this.validationError = null;
    return true;
  }

  /**
   * Gets validation error message
   * Implements AccountOwnerStrategy.getValidationError()
   */
  getValidationError(): string | null {
    return this.validationError;
  }

  /**
   * Gets the currently selected owner ID
   * Implements AccountOwnerStrategy.getSelectedOwner()
   */
  getSelectedOwner(): string | null {
    return this.selectedOwnerId;
  }

  /**
   * Sets the selected owner (for edit mode)
   * Implements AccountOwnerStrategy.setSelectedOwner()
   */
  setSelectedOwner(ownerId: string): void {
    WebLogger.log({ message: 'PersonOwner setSelectedOwner called', payload: { ownerId, isEditMode: this.isEditMode } });
    this.selectedOwnerId = ownerId;

    // Trigger change detection to update dropdown immediately
    this.cdr.detectChanges();
  }

  /**
   * Clears the current selection
   * Implements AccountOwnerStrategy.clearSelection()
   */
  clearSelection(): void {
    WebLogger.log({ message: "PersonOwner clearing selection" });
    this.selectedOwnerId = null;
    this.validationError = null;
  }

  /**
   * Gets owner data in standardized format
   * Implements AccountOwnerStrategy.getOwnerData()
   */
  getOwnerData(): PersonOwnerData | null {
    if (!this.selectedOwnerId) {
      return null;
    }

    // Determine owner type and details
    const isPrimary = this.primaryClient &&
                     (this.selectedOwnerId === this.primaryClient.piClientId ||
                      this.selectedOwnerId === this.primaryClient.uuid);

    const isSpouse = this.spouse &&
                    (this.selectedOwnerId === this.spouse.piClientId ||
                     this.selectedOwnerId === this.spouse.uuid);

    const isJointOwnership = Object.values(accountDetailsContent.JOINT_OWNERSHIP_TYPES)
                            .some(type => type.id === this.selectedOwnerId);

    let submitAccountOwner: string;
    let ownerUuid: string;
    let jointType: string | null = null;
    let ownerName: string;

    if (isPrimary) {
      submitAccountOwner = accountDetailsContent.ACCOUNT_OWNER_PRIMARY;
      ownerUuid = getUuid(this.primaryClient);
      ownerName = `${this.primaryClient.firstName} ${this.primaryClient.lastName}`;
    } else if (isSpouse) {
      submitAccountOwner = accountDetailsContent.ACCOUNT_OWNER_SECONDARY;
      ownerUuid = getUuid(this.spouse);
      ownerName = `${this.spouse.firstName} ${this.spouse.lastName}`;
    } else if (isJointOwnership) {
      submitAccountOwner = accountDetailsContent.ACCOUNT_OWNER_JOINT;
      ownerUuid = getUuid(this.primaryClient);
      jointType = Object.values(accountDetailsContent.JOINT_OWNERSHIP_TYPES)
                        .find(type => type.id === this.selectedOwnerId)?.value || null;
      ownerName = 'Joint Ownership';
    } else {
      // Fallback
      submitAccountOwner = accountDetailsContent.ACCOUNT_OWNER_PRIMARY;
      ownerUuid = this.selectedOwnerId;
      ownerName = 'Unknown';
    }

    const ownerData: PersonOwnerData = {
      ownerType: AccountOwnerType.INDIVIDUAL_BY_NAME,
      ownerId: submitAccountOwner,
      ownerUuid: ownerUuid,
      ownerName: ownerName,
      isPrimary: isPrimary,
      isSpouse: isSpouse,
      isJoint: isJointOwnership,
      jointType: jointType
    };

    WebLogger.log({ message: 'PersonOwner generated owner data', payload: ownerData });
    return ownerData;
  }

  /**
   * Gets display label for current selection
   */
  getSelectedOwnerLabel(): string {
    if (!this.selectedOwnerId) {
      return '';
    }

    const allItems = this.ownerOptions.groups[0]?.items || [];
    const selectedItem = allItems.find(item => item.id === this.selectedOwnerId);
    return selectedItem?.labelText || '';
  }

  /**
   * Handles person-specific submission logic
   * @param accountType The account type being submitted
   * @param isEditMode Whether this is an edit operation
   * @param globalStore Reference to global state store
   * @returns Object containing person submission data or null if validation fails
   */
  onSubmit(accountType: string, isEditMode: boolean, globalStore: any): any {
    WebLogger.log({ message: 'PersonOwner starting submission validation' });

    const personOwnerData = this.getOwnerData();

    if (!personOwnerData) {
      WebLogger.log({ message: "PersonOwner failed to get data for submission", level: "error" });
      return null;
    }

    WebLogger.log({ message: 'PersonOwner handling person submission', payload: {
      accountType,
      isEditMode,
      personData: personOwnerData,
      validationError: this.validationError,
    }});

    // Create person data for submission
    const personData = {
      ownerType: personOwnerData.ownerId, // PRIMARY, SECONDARY, or JOINT_OWNER
      ownerUuid: personOwnerData.ownerUuid,
      ownerName: personOwnerData.ownerName,
      isPrimary: personOwnerData.isPrimary,
      isSpouse: personOwnerData.isSpouse,
      isJoint: personOwnerData.isJoint,
      jointType: personOwnerData.jointType,
      isEdit: isEditMode
    };

    WebLogger.log({ message: 'PersonOwner submitting validated person data', payload: personData });

    // Update the global state with person data
    globalStore.updateGuidedFlowTempState('owner-type', personData);
    globalStore.mergeGuidedFlowTempState('owner-type');

    return personData;
  }
}
